import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPalette } from '../components/CommandPalette.tsx';
import type { CommandGroup } from '../components/CommandPalette.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { visibleNavItems } from './navigation.ts';
import { loadRecentPaths } from './recentPaths.ts';

/**
 * ⌘K palette wiring for the Shell. Sources, in priority order:
 *
 *   1. Request deep link — a `req_…` query jumps straight into the
 *      request logs with the id prefilled (the page reads `?requestId=`
 *      from the URL). Only offered when the tenant can see /requests.
 *   2. Recent — the last visited nav destinations (localStorage), shown
 *      only while the query is empty; once someone is typing, a stale
 *      list of unrelated pages is noise.
 *   3. Navigation — the permission-filtered nav items, substring
 *      matched on label AND path, so both "logs" and "/requests" find
 *      the same row.
 *
 * There is no account/entity search: that is an ops-console feature
 * with no tenant-facing endpoint behind it.
 */
interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

const REQUEST_ID_PATTERN = /^req_[a-z0-9]/i;

export function ShellCommandPalette({ open, onOpenChange }: Props): JSX.Element {
  const { user } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const trimmed = query.trim();
  const normalized = trimmed.toLowerCase();

  const groups = useMemo<readonly CommandGroup[]>(() => {
    const out: CommandGroup[] = [];
    const items = visibleNavItems(user);

    function go(path: string): void {
      onOpenChange(false);
      navigate(path);
    }

    const canSeeRequests = items.some((item) => item.to === '/requests');
    if (canSeeRequests && REQUEST_ID_PATTERN.test(trimmed)) {
      out.push({
        id: 'requests',
        heading: t('shell.cmdk.group.requests'),
        items: [
          {
            id: `req:${trimmed}`,
            label: t('shell.cmdk.open_request', { id: trimmed }),
            hint: t('shell.cmdk.hint.req'),
            onSelect: () => go(`/requests?requestId=${encodeURIComponent(trimmed)}`),
          },
        ],
      });
    }

    if (normalized.length === 0) {
      const recent = loadRecentPaths()
        .map((path) => items.find((item) => item.to === path))
        .filter((item): item is NonNullable<typeof item> => item !== undefined)
        .slice(0, 5);
      if (recent.length > 0) {
        out.push({
          id: 'recent',
          heading: t('shell.cmdk.group.recent'),
          items: recent.map((item) => ({
            id: `recent:${item.to}`,
            label: t(item.labelKey),
            hint: item.to,
            onSelect: () => go(item.to),
          })),
        });
      }
    }

    const matches = items.filter((item) => {
      if (normalized.length === 0) {
        return true;
      }
      return `${t(item.labelKey)} ${item.to}`.toLowerCase().includes(normalized);
    });
    if (matches.length > 0) {
      out.push({
        id: 'navigation',
        heading: t('shell.cmdk.group.navigation'),
        items: matches.map((item) => ({
          id: `nav:${item.to}`,
          label: t(item.labelKey),
          hint: item.to,
          onSelect: () => go(item.to),
        })),
      });
    }

    return out;
    // `open` is a real dependency, not a lint appeasement: recents are
    // read from localStorage, which the Shell writes in an effect AFTER
    // the render that resolves the session. Without re-sampling on the
    // open transition the palette would show the recents as they stood
    // one navigation ago — and nothing at all on the first page view.
  }, [open, trimmed, normalized, user, t, navigate, onOpenChange]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      groups={groups}
      search={query}
      onSearchChange={setQuery}
      placeholder={t('shell.cmdk.placeholder')}
      emptyText={t('shell.cmdk.empty')}
      ariaLabel={t('shell.cmdk.aria_label')}
      footer={t('shell.cmdk.footer_hint')}
      testId="shell-cmdk"
    />
  );
}
